import { ReflectUtils } from "@dooboostore/core";
import { SwcUtils } from "../utils/Utils.js";
import { resolveQueryElements, pickElements } from "./query.js";
import { getAttributeValue } from "./applyAttribute.js";
import { persistRead, persistReadAsync } from "./persistState.js";
const SWC_PARAMETER_METADATA_KEY = Symbol.for("simple-web-component:parameter");
const registerParam = (kind, target, propertyKey, parameterIndex, key) => {
  if (!propertyKey)
    return;
  const constructor = target.constructor;
  const saves = ReflectUtils.getOwnMetadata(SWC_PARAMETER_METADATA_KEY, constructor, propertyKey) ?? [];
  saves.push(key === void 0 ? { index: parameterIndex, kind } : { index: parameterIndex, kind, key });
  ReflectUtils.defineMetadata(SWC_PARAMETER_METADATA_KEY, saves, constructor, propertyKey);
};
function eventObject(target, propertyKey, parameterIndex) {
  registerParam("eventObject", target, propertyKey, parameterIndex);
}
function matchedElement(target, propertyKey, parameterIndex) {
  registerParam("matchedElement", target, propertyKey, parameterIndex);
}
function hostSet(target, propertyKey, parameterIndex) {
  registerParam("hostSet", target, propertyKey, parameterIndex);
}
function helperHostSet(target, propertyKey, parameterIndex) {
  registerParam("helperHostSet", target, propertyKey, parameterIndex);
}
function helperSet(target, propertyKey, parameterIndex) {
  registerParam("helperSet", target, propertyKey, parameterIndex);
}
function swcAppRouterEvent(target, propertyKey, parameterIndex) {
  registerParam("swcAppRouterEvent", target, propertyKey, parameterIndex);
}
function swcAppRouter(target, propertyKey, parameterIndex) {
  registerParam("swcAppRouter", target, propertyKey, parameterIndex);
}
function swcAppSimpleApplication(target, propertyKey, parameterIndex) {
  registerParam("swcAppSimpleApplication", target, propertyKey, parameterIndex);
}
function swcAppHost(target, propertyKey, parameterIndex) {
  registerParam("swcAppHost", target, propertyKey, parameterIndex);
}
const SWC_MESSAGE_FIELD_METADATA_KEY = Symbol.for("simple-web-component:message-field");
const applySwcAppMessage = (target, propertyKey, parameterIndex, options) => {
  if (typeof parameterIndex === "number")
    return registerParam("swcAppMessage", target, propertyKey, parameterIndex);
  if (propertyKey === void 0)
    return;
  const constructor = target.constructor;
  const list = ReflectUtils.getOwnMetadata(SWC_MESSAGE_FIELD_METADATA_KEY, constructor) ?? [];
  list.push({ propertyKey, ...options?.messageType ? { messageType: options.messageType } : {}, ...options?.subject ? { subject: options.subject } : {} });
  ReflectUtils.defineMetadata(SWC_MESSAGE_FIELD_METADATA_KEY, list, constructor);
};
const getSwcAppMessageFieldMetadata = (target) => {
  const constructor = target instanceof Function ? target : target?.constructor;
  if (!constructor)
    return [];
  return (ReflectUtils.findAllMetadata(SWC_MESSAGE_FIELD_METADATA_KEY, constructor) || []).flat();
};
function swcAppMessage(a, b, c) {
  if (typeof b === "string" || typeof b === "symbol")
    return applySwcAppMessage(a, b, c);
  const options = typeof a === "string" ? { ...b ?? {}, messageType: a } : { ...a ?? {} };
  return (target, propertyKey, parameterIndex) => applySwcAppMessage(target, propertyKey, parameterIndex, options);
}
function eventBeforeReturn(target, propertyKey, parameterIndex) {
  registerParam("eventBeforeReturn", target, propertyKey, parameterIndex);
}
function swcAppMessageBeforeReturn(target, propertyKey, parameterIndex) {
  registerParam("swcAppMessageBeforeReturn", target, propertyKey, parameterIndex);
}
function routeChangeBeforeReturn(target, propertyKey, parameterIndex) {
  registerParam("routeChangeBeforeReturn", target, propertyKey, parameterIndex);
}
function eventMediaBeforeReturn(target, propertyKey, parameterIndex) {
  registerParam("eventMediaBeforeReturn", target, propertyKey, parameterIndex);
}
function mutationObserverBeforeReturn(target, propertyKey, parameterIndex) {
  registerParam("mutationObserverBeforeReturn", target, propertyKey, parameterIndex);
}
function intersectionObserverBeforeReturn(target, propertyKey, parameterIndex) {
  registerParam("intersectionObserverBeforeReturn", target, propertyKey, parameterIndex);
}
function resizeObserverBeforeReturn(target, propertyKey, parameterIndex) {
  registerParam("resizeObserverBeforeReturn", target, propertyKey, parameterIndex);
}
function changedAttributeBeforeReturn(target, propertyKey, parameterIndex) {
  registerParam("changedAttributeBeforeReturn", target, propertyKey, parameterIndex);
}
function setTimeoutBeforeReturn(target, propertyKey, parameterIndex) {
  registerParam("setTimeoutBeforeReturn", target, propertyKey, parameterIndex);
}
function setIntervalBeforeReturn(target, propertyKey, parameterIndex) {
  registerParam("setIntervalBeforeReturn", target, propertyKey, parameterIndex);
}
function fetchPromise(target, propertyKey, parameterIndex) {
  registerParam("fetchPromise", target, propertyKey, parameterIndex);
}
function querySelectorParam(selector, options) {
  return (target, propertyKey, parameterIndex) => registerParam("querySelectorParam", target, propertyKey, parameterIndex, { selector, options });
}
function querySelectorAllParam(selector, options) {
  return (target, propertyKey, parameterIndex) => registerParam("querySelectorAllParam", target, propertyKey, parameterIndex, { selector, options });
}
function querySelectorShadowParam(selector, options) {
  return querySelectorParam(selector, { ...options ?? {}, root: "shadow" });
}
function querySelectorLightParam(selector, options) {
  return querySelectorParam(selector, { ...options ?? {}, root: "light" });
}
function querySelectorAllRootsParam(selector, options) {
  return querySelectorParam(selector, { ...options ?? {}, root: "all" });
}
function querySelectorAllShadowParam(selector, options) {
  return querySelectorAllParam(selector, { ...options ?? {}, root: "shadow" });
}
function querySelectorAllLightParam(selector, options) {
  return querySelectorAllParam(selector, { ...options ?? {}, root: "light" });
}
function querySelectorAllAllParam(selector, options) {
  return querySelectorAllParam(selector, { ...options ?? {}, root: "all" });
}
function attributeParam(selectorOrName, attrNameOrOptions, maybeOptions) {
  const withSelector = typeof attrNameOrOptions === "string";
  const attributeName = withSelector ? attrNameOrOptions : selectorOrName;
  const options = (withSelector ? maybeOptions : attrNameOrOptions) ?? {};
  const resolved = withSelector ? { ...options, selector: selectorOrName } : options;
  return (target, propertyKey, parameterIndex) => registerParam("attributeParam", target, propertyKey, parameterIndex, { attributeName, options: resolved });
}
const keyedStorageParam = (kind) => (key, options) => (target, propertyKey, parameterIndex) => registerParam(kind, target, propertyKey, parameterIndex, { key, options });
const localStorageParam = keyedStorageParam("localStorageParam");
const sessionStorageParam = keyedStorageParam("sessionStorageParam");
const cookieParam = keyedStorageParam("cookieParam");
const indexedDbParam = keyedStorageParam("indexedDbParam");
function fetchParam(input, init) {
  return (target, propertyKey, parameterIndex) => registerParam("fetchParam", target, propertyKey, parameterIndex, { input, init });
}
const getParameterMetadata = (target, propertyKey) => {
  const constructor = typeof target === "function" ? target : target.constructor;
  return ReflectUtils.getMetadata(SWC_PARAMETER_METADATA_KEY, constructor, propertyKey) ?? [];
};
const buildSwcParameterArgs = (target, propertyKey, kindValues, legacyArgs) => {
  const saves = getParameterMetadata(target, propertyKey);
  if (saves.length === 0)
    return legacyArgs;
  const maxIndex = Math.max(Math.max(...saves.map((s) => s.index)), legacyArgs.length - 1);
  const args = legacyArgs.slice(0, maxIndex + 1);
  for (const s of saves) {
    const value = kindValues[s.kind];
    args[s.index] = s.key !== void 0 && typeof value === "function" ? value(s.key) : value;
  }
  return args;
};
const simpleFetchJsonOrText = async (win, input, init) => {
  const fetchFn = typeof win?.fetch === "function" ? win.fetch.bind(win) : fetch;
  const res = await fetchFn(input, init);
  if (!res.ok)
    throw new Error(`[SWC] @fetchParam failed: ${res.status} ${String(input)}`);
  const text = await res.text();
  if (!text)
    return void 0;
  return (res.headers.get("Content-Type") ?? "").includes("json") ? JSON.parse(text) : text;
};
const buildDomStorageKindValues = (inst, win) => ({
  querySelectorParam: (cfg) => pickElements(resolveQueryElements(inst, cfg.selector, cfg.options ?? {}, win), cfg.options?.pick ?? "first"),
  querySelectorAllParam: (cfg) => pickElements(resolveQueryElements(inst, cfg.selector, cfg.options ?? {}, win), "all"),
  attributeParam: (cfg) => getAttributeValue(inst, cfg.attributeName, cfg.options),
  localStorageParam: (cfg) => persistRead(inst, cfg.key, { ...cfg.options, storage: "local" }),
  sessionStorageParam: (cfg) => persistRead(inst, cfg.key, { ...cfg.options, storage: "session" }),
  cookieParam: (cfg) => persistRead(inst, cfg.key, { ...cfg.options, storage: "cookie" }),
  indexedDbParam: (cfg) => persistReadAsync(inst, cfg.key, { ...cfg.options, storage: "indexeddb" }),
  fetchParam: (cfg) => {
    const helper = SwcUtils.getHelperAndHostSet(inst, win);
    const resolvedInput = typeof cfg.input === "function" ? cfg.input(inst, helper) : cfg.input;
    const resolvedInit = typeof cfg.init === "function" ? cfg.init(inst, helper) : cfg.init;
    return simpleFetchJsonOrText(win, resolvedInput, resolvedInit);
  }
});
const buildCommonKindValues = (inst, win) => {
  const hostSet2 = SwcUtils.getHostSet(inst);
  const helperSet2 = SwcUtils.getHelperSet(win);
  const helperHostSet2 = { ...helperSet2, ...hostSet2, $this: inst };
  return {
    hostSet: hostSet2,
    helperSet: helperSet2,
    helperHostSet: helperHostSet2,
    swcAppRouter: hostSet2.$appHost?.router,
    swcAppSimpleApplication: hostSet2.$appHost?.simpleApplication,
    swcAppHost: hostSet2.$appHost,
    ...buildDomStorageKindValues(inst, win)
  };
};
const SWC_ROUTE_FIELD_METADATA_KEY = Symbol.for("simple-web-component:route-field");
const isPathKind = (kind) => kind.includes("PathVariable");
const registerRouteField = (kind, target, propertyKey, key, options) => {
  if (isPathKind(kind) && !options?.path) {
    console.warn(`[SWC] @${kind} on field "${String(propertyKey)}" needs { path } (e.g. { path: '/user/{id}' }) \u2014 it will stay empty without it.`);
  }
  const constructor = target.constructor;
  const list = ReflectUtils.getOwnMetadata(SWC_ROUTE_FIELD_METADATA_KEY, constructor) ?? [];
  list.push({ propertyKey, kind, ...key !== void 0 ? { key } : {}, ...options?.path ? { path: options.path } : {} });
  ReflectUtils.defineMetadata(SWC_ROUTE_FIELD_METADATA_KEY, list, constructor);
};
const getSwcRouteFieldMetadata = (target) => {
  const constructor = target instanceof Function ? target : target?.constructor;
  if (!constructor)
    return [];
  return (ReflectUtils.findAllMetadata(SWC_ROUTE_FIELD_METADATA_KEY, constructor) || []).flat();
};
const applyRouteParam = (kind, target, propertyKey, parameterIndex, key, options) => {
  if (typeof parameterIndex === "number")
    return registerParam(kind, target, propertyKey, parameterIndex, key);
  if (propertyKey !== void 0)
    registerRouteField(kind, target, propertyKey, key, options);
};
const keyedQueryParam = (kind) => (key) => (target, propertyKey, parameterIndex) => applyRouteParam(kind, target, propertyKey, parameterIndex, key);
const keyedPathParam = (kind) => (key, options) => (target, propertyKey, parameterIndex) => applyRouteParam(kind, target, propertyKey, parameterIndex, key, options);
const queryParam = (kind) => (target, propertyKey, parameterIndex) => applyRouteParam(kind, target, propertyKey, parameterIndex);
const pathParam = (kind) => function(...args) {
  if (args.length >= 2)
    return applyRouteParam(kind, args[0], args[1], args[2]);
  const options = args[0];
  return (target, propertyKey, parameterIndex) => applyRouteParam(kind, target, propertyKey, parameterIndex, void 0, options);
};
const swcAppRouteQueryParam = keyedQueryParam("swcAppRouteQueryParam");
const swcAppRouteFirstQueryParam = keyedQueryParam("swcAppRouteFirstQueryParam");
const swcAppRouteLastQueryParam = keyedQueryParam("swcAppRouteLastQueryParam");
const swcAppRouteQueryParams = keyedQueryParam("swcAppRouteQueryParams");
const swcAppRouteQueryParamObject = queryParam("swcAppRouteQueryParamObject");
const swcAppRouteFirstQueryParamObject = queryParam("swcAppRouteFirstQueryParamObject");
const swcAppRouteLastQueryParamObject = queryParam("swcAppRouteLastQueryParamObject");
const swcAppRouteQueryParamsObject = queryParam("swcAppRouteQueryParamsObject");
const swcAppRouteURLSearchParams = queryParam("swcAppRouteURLSearchParams");
const swcAppRoutePathVariable = keyedPathParam("swcAppRoutePathVariable");
const swcAppRouteFirstPathVariable = keyedPathParam("swcAppRouteFirstPathVariable");
const swcAppRouteLastPathVariable = keyedPathParam("swcAppRouteLastPathVariable");
const swcAppRoutePathVariables = keyedPathParam("swcAppRoutePathVariables");
const swcAppRoutePathVariableObject = pathParam("swcAppRoutePathVariableObject");
const swcAppRouteFirstPathVariableObject = pathParam("swcAppRouteFirstPathVariableObject");
const swcAppRouteLastPathVariableObject = pathParam("swcAppRouteLastPathVariableObject");
const swcAppRoutePathVariablesObject = pathParam("swcAppRoutePathVariablesObject");
export {
  attributeParam,
  buildCommonKindValues,
  buildDomStorageKindValues,
  buildSwcParameterArgs,
  changedAttributeBeforeReturn,
  cookieParam,
  eventBeforeReturn,
  eventMediaBeforeReturn,
  eventObject,
  fetchParam,
  fetchPromise,
  getParameterMetadata,
  getSwcAppMessageFieldMetadata,
  getSwcRouteFieldMetadata,
  helperHostSet,
  helperSet,
  hostSet,
  indexedDbParam,
  intersectionObserverBeforeReturn,
  localStorageParam,
  matchedElement,
  mutationObserverBeforeReturn,
  querySelectorAllAllParam,
  querySelectorAllLightParam,
  querySelectorAllParam,
  querySelectorAllRootsParam,
  querySelectorAllShadowParam,
  querySelectorLightParam,
  querySelectorParam,
  querySelectorShadowParam,
  resizeObserverBeforeReturn,
  routeChangeBeforeReturn,
  sessionStorageParam,
  setIntervalBeforeReturn,
  setTimeoutBeforeReturn,
  swcAppHost,
  swcAppMessage,
  swcAppMessageBeforeReturn,
  swcAppRouteFirstPathVariable,
  swcAppRouteFirstPathVariableObject,
  swcAppRouteFirstQueryParam,
  swcAppRouteFirstQueryParamObject,
  swcAppRouteLastPathVariable,
  swcAppRouteLastPathVariableObject,
  swcAppRouteLastQueryParam,
  swcAppRouteLastQueryParamObject,
  swcAppRoutePathVariable,
  swcAppRoutePathVariableObject,
  swcAppRoutePathVariables,
  swcAppRoutePathVariablesObject,
  swcAppRouteQueryParam,
  swcAppRouteQueryParamObject,
  swcAppRouteQueryParams,
  swcAppRouteQueryParamsObject,
  swcAppRouteURLSearchParams,
  swcAppRouter,
  swcAppRouterEvent,
  swcAppSimpleApplication
};
//# sourceMappingURL=parameter.js.map
